import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getProductsSortedByAddedAtDesc } from "./utils/productSorter";
import {
  doc, getDoc, updateDoc, collection,
  query, where, getDocs, addDoc, serverTimestamp
} from "firebase/firestore";
import { db, auth } from "./firebase";
import SwipeWrapper from "./SwipeWrapper";
import Modal from "./Modal";
import AddToWishlistModal from './AddToWishlistModal';
import LazyImage from './LazyImage';
import { getAffiliateLink, trackClick } from "./utils/affiliateUtils";

export default function WishlistPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const from = location.state?.from || "/";
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [userWishlists, setUserWishlists] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportProductIndex, setReportProductIndex] = useState(null);
  const [reportSent, setReportSent] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const productRefs = useRef([]);

  const currentUser = auth.currentUser;

  useEffect(() => {
    async function fetchWishlist() {
      try {
        const ref = doc(db, "wishlists", id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setWishlist({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error("Errore caricamento wishlist:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchWishlist();
  }, [id]);

  const openModal = async (product, index, event) => {
    setSelectedProduct(product);

    const userId = currentUser?.uid;
    if (!userId) {
      setUserWishlists([]);
      setShowModal(true);
      return;
    }

    // Query per le wishlist di cui l'utente è proprietario
    const ownWishlistsQuery = query(
      collection(db, "wishlists"),
      where("userId", "==", userId),
      where("archived", "==", false)
    );

    // Query per le wishlist dove l'utente è collaboratore
    const collaboratorWishlistsQuery = query(
      collection(db, "wishlists"),
      where("collaborators", "array-contains", userId),
      where("archived", "==", false)
    );

    const [ownSnap, collaboratorSnap] = await Promise.all([
      getDocs(ownWishlistsQuery),
      getDocs(collaboratorWishlistsQuery)
    ]);

    const ownLists = ownSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isOwner: true 
    }));

    const collaboratorLists = collaboratorSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isOwner: false 
    }));

    const allLists = [...ownLists, ...collaboratorLists];
    setUserWishlists(allLists);
    setShowModal(true);
  };

  const addProductToWishlist = async (wishlistId) => {
    if (!selectedProduct) return;
    const ref = doc(db, "wishlists", wishlistId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const updated = Array.isArray(data.products)
      ? [selectedProduct, ...data.products]
      : [selectedProduct];
    await updateDoc(ref, { products: updated });
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setReportSent(false);
  };

  const openReportModal = (index) => {
    setReportProductIndex(index);
    setReportReason("");
    setShowReportModal(true);
    setReportSent(false);
  };

  const submitReport = async () => {
    if (!auth.currentUser) {
      alert(t('auth.warnings.loginRequired'));
      return;
    }
    const product = wishlist.products[reportProductIndex];
    await addDoc(collection(db, "reports"), {
      wishlistId: wishlist.id,
      productIndex: reportProductIndex,
      reportedBy: auth.currentUser.uid,
      reason: reportReason.trim(),
      createdAt: serverTimestamp(),
      productData: {
        title: product.title,
        image: product.image,
        url: product.url
      }
    });
    setReportSent(true);
    setTimeout(() => {
      setShowReportModal(false);
      setReportSent(false);
    }, 2000);
  };
    if (loading) return <div style={styles.message}>{t('common.loading')}</div>;
    if (!wishlist) return <div style={styles.message}>{t('wishlist.notFound')}</div>;

    const reasons = [
    { key: 'inappropriate', text: t('modals.reportReasons.inappropriate') },
    { key: 'irrelevant', text: t('modals.reportReasons.irrelevant') },
    { key: 'spam', text: t('modals.reportReasons.spam') },
    { key: 'other', text: t('modals.reportReasons.other') }
  ];

    return (
      <SwipeWrapper redirectTo={from}>
        <div style={styles.page}>
          <div style={styles.header}>
            <h2 style={styles.title}>{wishlist.name}</h2>
            <button onClick={() => navigate(-1)} style={styles.closeBtn}>
              {t('common.close')}
            </button>
          </div>

          {(wishlist.eventName || wishlist.eventDate) && (
            <div style={{ marginBottom: "10px", fontStyle: "italic", color: "#555" }}>
              {wishlist.eventName ? `${wishlist.eventName}: ` : ""}
              {wishlist.eventDate ? getCountdown(wishlist.eventDate) : ""}
            </div>
          )}

          {(wishlist.tags || []).length > 0 && (
            <div style={styles.tagContainer}>
              {wishlist.tags.map((tag, i) => (
                <span key={i} style={styles.tag}>#{tag}</span>
              ))}
            </div>
          )}

          <div style={styles.grid}>
            {(wishlist.products || []).map((p, i) => (
              <div
                key={i}
                ref={el => (productRefs.current[i] = el)}
                style={styles.card}
              >
                <LazyImage 
                  src={p.image} 
                  alt={p.title} 
                  style={styles.image} 
                />
                <div style={styles.cardContent}>
                  <a
                    href={getAffiliateLink(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...styles.link,
                      wordBreak: "break-all",
                      overflowWrap: "break-word",
                      hyphens: "auto",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: "1.3"
                    }}
                    onClick={() => trackClick(p.id, id, 'amazon')}
                  >
                    {p.title}
                  </a>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); openModal(p, i, e); }}
                      style={styles.addBtn}
                    >
                      ＋
                    </button>
                    <button
                      onClick={() => openReportModal(i)}
                      style={styles.menuBtn}
                    >
                      ⋮
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ✅ Modale Aggiungi */}
          <AddToWishlistModal
            showModal={showModal}
            onClose={closeModal}
            userWishlists={userWishlists}
            onAddToWishlist={addProductToWishlist}
          />

          {/* ✅ Modale Segnalazione */}
          <Modal
            title={reportSent ? t('modals.reportSent') : t('modals.reportImage')}
            showModal={showReportModal}
            onClose={closeReportModal}
            customContent={true}
          >
            <div style={styles.modalContent}>
              <h3 style={styles.modalTitle}>
                {reportSent ? t('modals.reportSent') : t('modals.reportImage')}
              </h3>
              {reportSent ? (
                <p style={{ textAlign: "center", margin: "20px 0" }}>
                  {t('modals.reportSentSuccess')}
                </p>
              ) : (
                <>
                  <div style={styles.reasonContainer}>
                    {reasons.map(reason => (
                      <button
                        key={reason.key}
                        style={{
                          ...styles.reasonBtn,
                          background: reportReason === reason.text ? "#000" : "#f5f5f5",
                          color: reportReason === reason.text ? "#fff" : "#000",
                        }}
                        onClick={() => setReportReason(reason.text)}
                      >
                        {reason.text}
                      </button>
                    ))}
                  </div>
                  <textarea
                    placeholder={t('modals.addDescription')}
                    value={reportReason === t('modals.reportReasons.other') ? reportReason : ""}
                    onChange={e => setReportReason(e.target.value)}
                    style={styles.textarea}
                  />
                  <div style={styles.modalActions}>
                    <button style={styles.confirmBtn} onClick={submitReport}>
                      {t('modals.send')}
                    </button>
                    <button style={styles.cancelBtn} onClick={closeReportModal}>
                      {t('common.cancel')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </Modal>
        </div>
      </SwipeWrapper>
    );
  }

  // 🔗 Funzione countdown evento
  function getCountdown(eventDate) {
    const now = new Date();
    const target = new Date(eventDate);
    const diff = target - now;
    if (diff <= 0) return "Evento passato";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    return `${days}g ${hours}h ${minutes}m`;
  }
const styles = {
  page: {
    padding: "20px",
    paddingTop: "35px",
    fontFamily: "Poppins, sans-serif",
    background: "#fff",
    minHeight: "100vh",
    color: "#000",
    paddingBottom: "80px",
  },
  modalContent: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "280px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflow: "hidden",
    margin: "auto"
  },

  message: {
    padding: "20px",
    fontFamily: "Poppins, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    margin: 0,
    marginTop: "12px",
  },
  closeBtn: {
    background: "#000",
    color: "#fff",
    border: "none",
    padding: "6px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    marginTop: "12px",
  },
  tagContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginBottom: "20px",
  },
  tag: {
    fontSize: "12px",
    background: "#eee",
    padding: "4px 8px",
    borderRadius: "20px",
    color: "#333",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "20px",
  },
  card: {
    background: "#f9f9f9",
    border: "1px solid #ddd",
    borderRadius: "10px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  },
  image: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
  },
  cardContent: {
    padding: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  link: {
    fontWeight: "500",
    fontSize: "16px",
    textDecoration: "none",
    color: "#000",
    flex: 1,
    wordWrap: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%"
  },
  addBtn: {
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    width: "32px",
    height: "32px",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  menuBtn: {
    background: "none",
    border: "none",
    fontSize: "22px",
    cursor: "pointer",
    color: "#000",
  },

  modalTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#000",
    margin: 0
  },
  reasonContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px"
  },
  reasonBtn: {
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    cursor: "pointer",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
    width: "100%",
    boxSizing: "border-box"
  },
  textarea: {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    minHeight: "80px",
    resize: "none",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif",
    fontSize: "14px",
    outline: "none"
  },
  modalActions: {
    display: "flex",
    gap: "12px"
  },
  modalBtn: {
    width: "100%",
    padding: "10px",
    marginBottom: "10px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer",
  },
  modalCancel: {
    width: "100%",
    padding: "10px",
    background: "#e0e0e0",
    color: "#000",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer"
  },
  confirmBtn: {
    background: "#000",
    color: "#fff",
    border: "none",
    padding: "12px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    flex: 1,
    fontSize: "14px",
    fontWeight: "500"
  },
  cancelBtn: {
    background: "#f5f5f5",
    color: "#000",
    border: "1px solid #ddd",
    padding: "12px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    flex: 1,
    fontSize: "14px",
    fontWeight: "500"
  },

};